import * as Popover from '@radix-ui/react-popover';
import cn from 'classnames';
import { useState } from 'react';

import { DEFAULT_LOCALE } from '@/shared/config';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { useLanguages } from '@/shared/hooks/useLanguages';
import { trackEvent } from '@/shared/lib';
import { Icon } from '@/shared/ui';

import { userModel } from '@/entities/user';

import { ICON_SIZE, MOBILE_ICON_SIZE } from '../../config/constants';

import styles from './LanguageSelector.module.scss';
import { LanguageRow } from './ui/LanguageRow/LanguageRow';

type LanguageSelectorProps = {
  className?: string;
  isMobile?: boolean;
  testID?: string;
};

export const LanguageSelector = (props: LanguageSelectorProps) => {
  const { className, isMobile } = props;
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const dispatch = useAppDispatch();

  const userID = useAppSelector(userModel.selectors.selectCurrentUserID);

  const userLocale: string =
    useAppSelector(userModel.selectors.selectUserLocale) ?? DEFAULT_LOCALE;

  const { languageOptions } = useLanguages();

  const changeLanguageHandle = async (languageLocale: string) => {
    setIsOpen(false);

    trackEvent({
      eventName: 'AllPagesLanguageChangeClicked',
      properties: {
        language: languageLocale,
      },
    });

    const { User } = await dispatch(
      userModel.thunks.setUserLanguage({
        UserID: userID,
        Language: languageLocale,
      }),
    ).unwrap();

    if (User) {
      dispatch(userModel.actions.setOne(User));
    }
  };

  const togglePopover = (open: boolean) => {
    setIsOpen(open);
  };

  return (
    <Popover.Root open={isOpen} onOpenChange={togglePopover}>
      <Popover.Trigger asChild>
        <button
          type="button"
          data-component="language-selector"
          className={cn(styles.worldIconContainer, className)}
          aria-label="Change language"
        >
          <Icon
            name="world"
            size={isMobile ? MOBILE_ICON_SIZE : ICON_SIZE}
            className={styles.worldIcon}
          />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content
          className={styles.content}
          sideOffset={5}
          align="start"
          collisionPadding={{ right: 32 }}
        >
          <div className={styles.dropdownContainer}>
            {languageOptions.map((language) => (
              <LanguageRow
                languageName={language.label}
                changeLanguage={() => changeLanguageHandle(language.value)}
                key={language.value}
                checked={language.value === userLocale}
              />
            ))}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
};
